/**
 * ZeoConversation - zentraler, ruhiger Chatbereich mit viel Weissraum.
 *
 * ZEO interaktive Antworten (06.10.2026): jede Nachricht und jeder
 * einzelne Antwort-Block ist per Hover referenzierbar ("Antworten"),
 * Dokument-Referenzen werden als anklickbare Karten dargestellt. Legacy-
 * Nachrichten ohne document_refs/Mehrfach-Bloecke bleiben unveraendert als
 * reines Markdown darstellbar (Backward Compatibility, Abschnitt 13/19 G).
 */
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { betreuungAPI } from '@/api/betreuungAPI';
import ZeoIcon from '@/components/ZeoIcon';
import DocumentPreviewModal from '@/components/DocumentPreviewModal';
import { Loader2, Reply, FileText, ExternalLink, Plus, Check, AlertTriangle, X, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { formatGroesse, typLabel } from './zeoAnhang';
import { dokumentKartenInfo, dokumentFuerUiAktion, teileWeitere } from './zeoWorkspace';
import ZeoAufgabenListe from '@/components/aufgaben/ZeoAufgabenListe';
import { ZeoFrageAntwort } from '@/components/aufgaben/AufgabenZeoFrage';
import ZeoSchreibEntwurfKarte from './ZeoSchreibEntwurfKarte';
import { umschalten, nur } from '@/lib/aufgabenAuswahl';


// Antworten ab dieser Laenge werden zunaechst kompakt dargestellt ("Mehr anzeigen"). Der VOLLSTAENDIGE Text steckt immer im DOM
// (reine CSS-Begrenzung, kein Abschneiden, kein neuer LLM-Aufruf). Normale/mittellange Antworten werden nie eingeklappt.
export const LANGE_ANTWORT_ZEICHEN = 6000;

function textVon(children) {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(textVon).join('');
  return textVon(children.props?.children);
}

/** Markdown (GFM-Tabellen, Listen) in voller Breite; Tabellen horizontal scrollbar; jeder Listenpunkt ist per Hover referenzierbar. */
function AntwortMarkdown({ msg, onReply }) {
  const components = {
    table: ({ children }) => (<div className="overflow-x-auto my-3 rounded-lg border border-slate-200" data-testid="zeo-md-table-wrap"><table className="min-w-full text-sm border-collapse">{children}</table></div>),
    thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
    th: ({ children, style }) => <th style={style} className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700 align-top">{children}</th>,
    td: ({ children, style }) => <td style={style} className="border-t border-slate-100 px-3 py-1.5 align-top text-slate-700">{children}</td>,
    li: ({ node, children }) => {
      const offset = node?.position?.start?.offset;
      return (
        <li className="group/block relative">
          {children}
          {onReply && offset != null && (
            <button
              type="button"
              onClick={() => onReply(msg, { block_id: `${msg.id}:li${offset}`, display_text: textVon(children).trim() })}
              className="absolute -left-6 top-0.5 opacity-0 group-hover/block:opacity-100 transition-opacity p-1 rounded-full text-slate-300 hover:text-violet-600 hover:bg-violet-100"
              title="Darauf antworten"
              data-testid="zeo-reply-block"
            >
              <Reply size={12} />
            </button>
          )}
        </li>
      );
    },
  };
  return (
    <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed [&>p]:my-2 [&>ul]:my-2 [&>ol]:my-2 [&_ul]:pl-6 [&_ol]:pl-6 break-words" data-testid="zeo-antwort-text">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{msg.text || ''}</ReactMarkdown>
    </div>
  );
}

/** Anhang einer Chatnachricht: Thumbnail/Datei-Chip; Klick oeffnet die Ansicht (Abruf nur ueber die zugehoerige Session, mit Login). */
function AnhangChip({ anhang, sessionId, onOeffnen }) {
  const istBildAnhang = anhang.art === 'bild' || (anhang.mime || '').startsWith('image/');
  const [thumb, setThumb] = useState(anhang.vorschau || null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let url = null; let abgebrochen = false;
    if (!istBildAnhang || anhang.vorschau || !sessionId || !anhang.id || String(anhang.id).startsWith('local')) return undefined;
    betreuungAPI.zeoAnhangBlob(sessionId, anhang.id).then((b) => { if (!abgebrochen) { url = URL.createObjectURL(b); setThumb(url); } }).catch(() => setFehler(true));
    return () => { abgebrochen = true; if (url) URL.revokeObjectURL(url); };
  }, [anhang.id, anhang.vorschau, istBildAnhang, sessionId]);
  return (
    <button
      type="button"
      onClick={() => onOeffnen(anhang, thumb)}
      className="flex items-center gap-2 text-left text-xs rounded-xl border border-violet-100 bg-white/80 hover:bg-white px-2 py-1.5 max-w-[260px]"
      data-testid="zeo-nachricht-anhang"
      title="Anhang öffnen"
    >
      {thumb ? <img src={thumb} alt="" className="w-12 h-12 rounded object-cover shrink-0" data-testid="zeo-nachricht-anhang-thumb" /> : <FileText size={20} className="text-violet-500 shrink-0" />}
      <span className="min-w-0">
        <span className="block truncate text-slate-700" data-testid="zeo-nachricht-anhang-name">{anhang.name}</span>
        <span className="block text-slate-400">{anhang.typ || typLabel({ name: anhang.name })} · {formatGroesse(anhang.groesse)}{fehler ? ' · nicht verfügbar' : ''}{anhang.extraktion && anhang.extraktion !== 'ok' ? ' · kein Text erkannt' : ''}</span>
      </span>
    </button>
  );
}

function AnhangAnsicht({ anhang, thumb, sessionId, onSchliessen }) {
  const [url, setUrl] = useState(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    let u = null; let abgebrochen = false;
    if (!anhang) return undefined;
    betreuungAPI.zeoAnhangBlob(sessionId, anhang.id).then((b) => { if (!abgebrochen) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => setFehler(true));
    return () => { abgebrochen = true; if (u) URL.revokeObjectURL(u); setUrl(null); setFehler(false); };
  }, [anhang, sessionId]);
  if (!anhang) return null;
  const mime = anhang.mime || '';
  const bild = anhang.art === 'bild' || mime.startsWith('image/');
  const pdfOderText = mime === 'application/pdf' || mime === 'text/plain' || anhang.art === 'pdf' || anhang.art === 'text';
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onSchliessen} data-testid="zeo-anhang-ansicht">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <div className="font-medium text-slate-800 truncate flex-1" data-testid="zeo-anhang-ansicht-name">{anhang.name}</div>
          {url && <a href={url} download={anhang.name} className="text-slate-500 hover:text-violet-600" title="Herunterladen"><Download size={16} /></a>}
          <button type="button" onClick={onSchliessen} className="text-slate-400 hover:text-slate-700" data-testid="zeo-anhang-ansicht-schliessen"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto min-h-[40vh] flex items-center justify-center bg-slate-50">
          {fehler && <p className="text-sm text-slate-500 p-6">Anhang nicht verfügbar.</p>}
          {!fehler && !url && <Loader2 className="animate-spin text-slate-400" />}
          {url && bild && <img src={url} alt={anhang.name} className="max-w-full max-h-[80vh] object-contain" data-testid="zeo-anhang-ansicht-bild" />}
          {url && !bild && pdfOderText && <iframe title={anhang.name} src={url} sandbox="" className="w-full h-[80vh] bg-white" data-testid="zeo-anhang-ansicht-frame" />}
          {url && !bild && !pdfOderText && <p className="text-sm text-slate-600 p-6">Vorschau nicht möglich – bitte herunterladen.</p>}
        </div>
      </div>
    </div>
  );
}

/** Aufgabenkarte (Batch 2): "Aufgabe oeffnen" oeffnet die KANONISCHE Olympus-Aufgabenmaske -- keine ZEO-Schattenansicht. Keine technische ID in der UI. */
function DokumentKarte({ dokumentRef, scope, aktuelleKlientId, onOeffnen, onImChatVerwenden, istAusgewaehlt }) {
  const konflikt = scope === 'client' && aktuelleKlientId && dokumentRef.klient_id && dokumentRef.klient_id !== aktuelleKlientId;
  const unzugeordnet = !dokumentRef.klient_id;
  const info = dokumentKartenInfo(dokumentRef);

  return (
    <div
      className={`mt-2 border rounded-xl p-3 flex items-start gap-3 max-w-md ${konflikt ? 'border-amber-300 bg-amber-50' : 'border-violet-100 bg-white'}`}
      data-testid="zeo-document-card"
    >
      <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
        <FileText size={18} className="text-violet-500" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate" data-testid="zeo-card-titel">{info.titel}</p>
        <p className="text-xs text-slate-400 mt-0.5" data-testid="zeo-card-meta">
          {info.art || 'Dokument'}{dokumentRef.absender ? ` · ${dokumentRef.absender}` : ''}
        </p>
        {info.datumsZeilen.length > 0 && (
          <p className="text-xs text-slate-500" data-testid="zeo-card-daten">{info.datumsZeilen.join(' · ')}</p>
        )}
        {info.klient && <p className="text-xs text-slate-400" data-testid="zeo-card-klient">Klient: {info.klient}</p>}
        {dokumentRef.beleg ? (
          <dl className="mt-1 text-xs grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5" data-testid="zeo-card-beleg">
            <dt className="text-slate-400">Was:</dt><dd className="text-slate-700" data-testid="zeo-card-beleg-was">{dokumentRef.beleg.was}</dd>
            <dt className="text-slate-400">Belegt:</dt>
            <dd className="text-slate-700" data-testid="zeo-card-beleg-aussage">{dokumentRef.beleg.aussage || 'keine einzelne Aussage zugeordnet'}</dd>
            <dt className="text-slate-400">Relevanz:</dt><dd className="text-violet-700" data-testid="zeo-card-beleg-relevanz">{dokumentRef.beleg.relevanz}</dd>
            <dt className="text-slate-400">Original:</dt><dd className="text-slate-700 break-all" data-testid="zeo-card-beleg-original">{dokumentRef.beleg.original}</dd>
          </dl>
        ) : (
          dokumentRef.grund && <p className="text-xs text-violet-700" data-testid="zeo-card-grund">{dokumentRef.grund}</p>
        )}
        {info.betrifftPerson && (
          <p className="text-xs text-amber-700 mt-1 flex items-start gap-1" data-testid="zeo-card-andere-person">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>Dokument einer anderen Person: {info.betrifftPerson}{info.betrifftBeleg ? ` (Beleg: ${info.betrifftBeleg})` : ' (Beziehung zum Klienten nicht belegt)'}</span>
          </p>
        )}
        {konflikt && (
          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1" data-testid="zeo-document-conflict">
            <AlertTriangle size={12} /> Dieses Dokument ist aktuell einem anderen Klienten zugeordnet.
          </p>
        )}
        {unzugeordnet && (
          <p className="text-xs text-slate-400 mt-1">Klient unbekannt</p>
        )}
        <div className="flex gap-2 mt-2">
          {info.originalVerfuegbar ? (
            <button
              type="button"
              onClick={() => onOeffnen(dokumentRef)}
              className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center gap-1"
            >
              <ExternalLink size={12} /> Dokument öffnen
            </button>
          ) : (
            <span className="text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 flex items-center gap-1" data-testid="zeo-original-nicht-verfuegbar">
              <AlertTriangle size={12} /> Original nicht verfügbar
            </span>
          )}
          {!konflikt && (
            <button
              type="button"
              onClick={() => onImChatVerwenden(dokumentRef)}
              className={`text-xs px-2 py-1 rounded-md flex items-center gap-1 ${istAusgewaehlt ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
            >
              {istAusgewaehlt ? <Check size={12} /> : <Plus size={12} />} Im Chat verwenden
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const istLang = (msg) => (msg.text || '').length > LANGE_ANTWORT_ZEICHEN;

export default function ZeoConversation({
  messages, loading, scope, klientName, aktuelleKlientId, sessionId,
  onReply, selectedDocumentIds, onToggleDocument, onAktivesDokument, onAufgabeOeffnen,
  aufgabenAuswahl = new Map(), onAufgabenAuswahl, onAufgabenZusammenfuehren, onAufgabenErledigen, aufgabenStand = 0,
}) {
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  const nahUnten = useRef(true);                     // Nutzerposition respektieren: nur nachfuehren, wenn der Nutzer am Ende liest
  const letzteAnzahl = useRef(0);
  const [aufgeklappt, setAufgeklappt] = useState({});
  const [anhangAnsicht, setAnhangAnsicht] = useState(null);
  const [previewDokument, setPreviewDokument] = useState(null);
  // UI-Aktion OPEN_DOCUMENT (ZEO "oeffne dieses Dokument"): oeffnet die kanonische Dokumentansicht fuer NEUE Antworten (beim Laden einer alten Sitzung nicht erneut)
  const bekannteNachrichten = useRef(null);
  useEffect(() => {
    const ids = new Set((messages || []).map((m) => m.id));
    if (bekannteNachrichten.current === null) { bekannteNachrichten.current = ids; return; }
    const neu = (messages || []).filter((m) => m.id && !bekannteNachrichten.current.has(m.id));
    bekannteNachrichten.current = ids;
    for (const m of neu) {
      const ref = dokumentFuerUiAktion(m);
      if (ref) { setPreviewDokument(ref); if (onAktivesDokument) onAktivesDokument(ref); }
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const n = (messages || []).length;
    const letzte = (messages || [])[n - 1];
    const eigeneNachricht = letzte && letzte.rolle === 'user';
    const frischGeladen = letzteAnzahl.current === 0 && n > 0;
    letzteAnzahl.current = n;
    if (frischGeladen || eigeneNachricht || nahUnten.current) endRef.current?.scrollIntoView({ behavior: frischGeladen ? 'auto' : 'smooth', block: 'end' });
  }, [messages, loading]);

  if (!messages || messages.length === 0) {
    return (
      <>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-violet-50 flex items-center justify-center">
              <ZeoIcon size={36} />
            </div>
            <h2 className="text-lg font-semibold text-slate-700 mb-1">
              {scope === 'client' ? `Bereit für ${klientName || 'diesen Fall'}` : 'Was ist heute wichtig?'}
            </h2>
            <p className="text-sm text-slate-400">
              {scope === 'client'
                ? 'Frage nach offenen Aufgaben, Fristen, Vorgängen oder lass dir den Fall zusammenfassen.'
                : 'Frage büroweit nach dringenden Aufgaben, Fristen oder Vorgängen.'}
            </p>
          </div>
        </div>
        <DocumentPreviewModal dokument={previewDokument} onClose={() => setPreviewDokument(null)} />
      </>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => { const el = e.currentTarget; nahUnten.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160; }}
      className="flex-1 min-h-0 overflow-y-auto px-6 lg:px-10 pt-8 pb-10" data-testid="zeo-conversation"
    >
      <div className="mx-auto w-full max-w-[min(100%,96rem)] flex flex-col gap-6" data-testid="zeo-conversation-spalte">
        {messages.map((msg) => {
          return (
            <div
              key={msg.id || `${msg.timestamp}-${msg.rolle}`}
              className={`group flex ${msg.rolle === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.rolle === 'user' ? (
                <div className="flex items-start gap-2 max-w-[min(75%,64rem)]">
                  <button
                    type="button"
                    onClick={() => onReply?.(msg, null)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full text-slate-300 hover:text-violet-600 hover:bg-violet-50 mt-1"
                    title="Antworten"
                    data-testid="zeo-reply-user"
                  >
                    <Reply size={14} />
                  </button>
                  <div
                    className="rounded-2xl px-4 py-3 text-[15px] leading-relaxed min-w-0"
                    style={{ background: 'rgba(139,92,246,0.10)', color: '#312e81' }}
                  >
                    <div className="whitespace-pre-wrap break-words" data-testid="zeo-user-text">{msg.text}</div>
                    {(msg.anhaenge || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid="zeo-nachricht-anhaenge">
                        {msg.anhaenge.map((a) => <AnhangChip key={a.id} anhang={a} sessionId={sessionId} onOeffnen={(an, th) => setAnhangAnsicht({ ...an, thumb: th })} />)}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-w-0 flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center shrink-0 mt-0.5">
                    <ZeoIcon size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-start gap-1.5">
                      <div
                        className={`flex-1 min-w-0 relative ${istLang(msg) && !aufgeklappt[msg.id] ? 'max-h-[28rem] overflow-hidden' : ''}`}
                        data-testid="zeo-antwort"
                        data-lang={istLang(msg) ? 'true' : 'false'}
                      >
                        <AntwortMarkdown msg={msg} onReply={onReply} />
                        {istLang(msg) && !aufgeklappt[msg.id] && (
                          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#f8f6ff] to-transparent pointer-events-none" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => onReply?.(msg, null)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full text-slate-300 hover:text-violet-600 hover:bg-violet-50 shrink-0 mt-0.5"
                        title="Antworten"
                        data-testid="zeo-reply-message"
                      >
                        <Reply size={14} />
                      </button>
                    </div>
                    {istLang(msg) && (
                      <button
                        type="button"
                        onClick={() => setAufgeklappt((p) => ({ ...p, [msg.id]: !p[msg.id] }))}
                        className="mt-1 flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800"
                        data-testid="zeo-antwort-toggle"
                      >
                        {aufgeklappt[msg.id] ? <><ChevronUp size={14} /> Weniger anzeigen</> : <><ChevronDown size={14} /> Mehr anzeigen</>}
                      </button>
                    )}

                    {(msg.task_refs || []).length > 0 && (
                      <ZeoAufgabenListe refs={msg.task_refs} msgId={msg.id} auswahl={aufgabenAuswahl} stand={aufgabenStand}
                        onAuswahl={(m) => onAufgabenAuswahl && onAufgabenAuswahl(m)} onOeffnen={onAufgabeOeffnen} />
                    )}
                    {msg.schreib_entwurf && <ZeoSchreibEntwurfKarte entwurf={msg.schreib_entwurf} messageId={msg.schreib_message_id || msg.id} />}
                    {msg.aufgaben_frage && (
                      <div className="mt-2 max-w-2xl bg-white border border-violet-100 rounded-xl p-3" data-testid="zeo-aufgaben-frage-antwort">
                        <ZeoFrageAntwort antwort={msg.aufgaben_frage} auswahl={aufgabenAuswahl} ohneKurzantwort
                          onToggle={(a) => onAufgabenAuswahl && onAufgabenAuswahl(umschalten(aufgabenAuswahl, a))}
                          onOpen={(id) => onAufgabeOeffnen && onAufgabeOeffnen({ id })}
                          onSelect={(liste) => onAufgabenAuswahl && onAufgabenAuswahl(nur(liste))}
                          onMerge={onAufgabenZusammenfuehren} onErledigen={onAufgabenErledigen} />
                      </div>
                    )}
                    {(msg.document_refs || []).map((ref) => (
                      <DokumentKarte
                        key={ref.id}
                        dokumentRef={ref}
                        scope={scope}
                        aktuelleKlientId={aktuelleKlientId}
                        onOeffnen={(ref) => { setPreviewDokument(ref); if (onAktivesDokument) onAktivesDokument(ref); }}
                        onImChatVerwenden={(ref) => { onToggleDocument(ref); if (onAktivesDokument) onAktivesDokument(ref); }}
                        istAusgewaehlt={(selectedDocumentIds || []).includes(ref.id)}
                      />
                    ))}
                    {(() => {
                      // 31.10.2026: B = weitere relevante Vorgangsunterlagen (eingeklappt), C = sonstige Suchtreffer (standardmaessig verborgen)
                      const { relevant, sonstige } = teileWeitere(msg.weitere_dokument_refs);
                      const karte = (ref) => (
                        <DokumentKarte
                          key={ref.id}
                          dokumentRef={ref}
                          scope={scope}
                          aktuelleKlientId={aktuelleKlientId}
                          onOeffnen={(r) => { setPreviewDokument(r); if (onAktivesDokument) onAktivesDokument(r); }}
                          onImChatVerwenden={(r) => { onToggleDocument(r); if (onAktivesDokument) onAktivesDokument(r); }}
                          istAusgewaehlt={(selectedDocumentIds || []).includes(ref.id)}
                        />
                      );
                      return (
                        <>
                          {relevant.length > 0 && (
                            <details className="mt-2 max-w-md" data-testid="zeo-weitere-treffer">
                              <summary className="text-xs text-slate-500 cursor-pointer select-none">
                                Weitere relevante Vorgangsunterlagen ({relevant.length}) – nicht als Beleg verwendet
                              </summary>
                              {relevant.map(karte)}
                            </details>
                          )}
                          {sonstige.length > 0 && (
                            <details className="mt-1 max-w-md" data-testid="zeo-sonstige-treffer">
                              <summary className="text-xs text-slate-400 cursor-pointer select-none">
                                Sonstige Suchtreffer anzeigen ({sonstige.length}) – ohne erkennbaren Bezug zur Frage
                              </summary>
                              {sonstige.map(karte)}
                            </details>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center shrink-0">
              <ZeoIcon size={20} />
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" /> ZEO denkt nach…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <DocumentPreviewModal dokument={previewDokument} onClose={() => setPreviewDokument(null)} />
      <AnhangAnsicht anhang={anhangAnsicht} thumb={anhangAnsicht?.thumb} sessionId={sessionId} onSchliessen={() => setAnhangAnsicht(null)} />
    </div>
  );
}
